import { passwordStrength as checkPasswordStrength } from "check-password-strength";
import SimpleModal from "app/components/Modals/SimpleModal";
import Button from "app/storybookComponents/Button";
import { useEffect, useState, useCallback } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  resetPassword,
  resetResetPasswordStatus,
  selectResetPasswordError,
  selectResetPasswordStatus,
  setResetPasswordError,
} from "./slice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getEyeSlashed,
  getInputPlaceholder,
  getInputType,
} from "../Login/helpers";
import PasswordStrengthBar from "../Login/PasswordStrengthBar";
import { PASSWORD_STRENGTH_OPTIONS } from "../Login/constants";

interface ResetPasswordModalProps {
  show: boolean;
  handleClose: () => void;
}

const ResetPasswordModal = ({ show, handleClose }: ResetPasswordModalProps) => {
  const dispatch = useAppDispatch();
  const resetPasswordStatus = useAppSelector(selectResetPasswordStatus);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [inputVisibilityMap, setInputVisibilityMap] = useState<
    Record<string, boolean>
  >({});
  const resetPasswordError = useAppSelector(selectResetPasswordError);
  const [mismatchedPasswords, setMismatchedPasswords] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<string | null>(null);

  const clearErrors = useCallback(() => {
    setMismatchedPasswords(false);
    dispatch(setResetPasswordError(null));
  }, [dispatch]);

  const onClose = useCallback(() => {
    if (resetPasswordStatus === "loading") return;
    handleClose();
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordStrength(null);
    clearErrors();
  }, [
    resetPasswordStatus,
    handleClose,
    setCurrentPassword,
    setNewPassword,
    setConfirmPassword,
    clearErrors,
  ]);

  useEffect(() => {
    if (resetPasswordStatus !== "succeeded") {
      return;
    }
    onClose();
    dispatch(resetResetPasswordStatus());
  }, [resetPasswordStatus, dispatch, onClose]);

  const onSubmit = () => {
    if (newPassword !== confirmPassword) {
      setMismatchedPasswords(true);
      return;
    }

    dispatch(
      resetPassword({
        currentPassword,
        newPassword,
      })
    );
  };

  const getErrorBanner = () => {
    if (mismatchedPasswords) {
      return (
        <div className="warning-banner red">
          <p>
            <FontAwesomeIcon
              icon={["far", "exclamation-triangle"]}
              className="me-2"
            />
            Passwords do not match
          </p>
        </div>
      );
    }

    if (resetPasswordError) {
      return (
        <div className="warning-banner red">
          <p>
            <FontAwesomeIcon
              icon={["far", "exclamation-triangle"]}
              className="me-2"
            />
            {resetPasswordError}
          </p>
        </div>
      );
    }
    return null;
  };

  const onNewPasswordChange = (e: string) => {
    setNewPassword(e);
    if (!e) {
      setPasswordStrength(null);
      return;
    }

    const strength = checkPasswordStrength(e, PASSWORD_STRENGTH_OPTIONS);
    setPasswordStrength(strength.value);
  };

  const getFormInput = (
    label: string,
    value: string,
    onChange: (e: string) => void,
    controlId: string,
    hasError?: boolean
  ) => {
    const showPassword = inputVisibilityMap[controlId];
    return (
      <Form.Group controlId={controlId}>
        <Form.Label>{label}</Form.Label>
        <InputGroup>
          <Form.Control
            value={value}
            onChange={(e) => {
              clearErrors();
              onChange(e.target.value);
            }}
            placeholder={getInputPlaceholder(showPassword)}
            type={getInputType(showPassword)}
            className={`login-input-field ${hasError ? "has-error" : ""}`}
          />
          <InputGroup.Text>
            <button
              type="button"
              onClick={() => {
                setInputVisibilityMap({
                  ...inputVisibilityMap,
                  [controlId]: !showPassword,
                });
              }}
              className="no-style-button"
            >
              <FontAwesomeIcon icon={["far", getEyeSlashed(showPassword)]} />
            </button>
          </InputGroup.Text>
        </InputGroup>
      </Form.Group>
    );
  };

  const getBody = () => {
    return (
      <Form>
        {getFormInput(
          "Current Password",
          currentPassword,
          (e) => setCurrentPassword(e),
          "currentPassword"
        )}
        <div style={{ position: "relative" }}>
          {getFormInput(
            "New Password",
            newPassword,
            onNewPasswordChange,
            "newPassword",
            mismatchedPasswords ||
              passwordStrength === "Too weak" ||
              passwordStrength === "Weak"
          )}
          <PasswordStrengthBar passwordStrength={passwordStrength} />
        </div>
        {getFormInput(
          "Confirm New Password",
          confirmPassword,
          (e) => setConfirmPassword(e),
          "confirmPassword",
          mismatchedPasswords
        )}
        {getErrorBanner()}
        <Form.Group className="row-gap-12px ms-auto">
          <Button
            variant="secondary-blue"
            className="border-0"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={(e) => {
              e.preventDefault();
              onSubmit();
            }}
            disabled={
              resetPasswordStatus === "loading" ||
              mismatchedPasswords ||
              !!resetPasswordError ||
              passwordStrength === "Too weak" ||
              passwordStrength === "Weak"
            }
          >
            {resetPasswordStatus === "loading" ? "Saving..." : "Save"}
          </Button>
        </Form.Group>
      </Form>
    );
  };
  return (
    <SimpleModal show={show} onHide={onClose} title="Change Password">
      {getBody()}
    </SimpleModal>
  );
};

export default ResetPasswordModal;
